import styled, { keyframes } from "styled-components";
import { mediaMax } from "components/designV2/breakpoints";
import { Spacing } from "components/designV2/constants";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";

const getStatusBackgroundColor = (status: string): string => {
  if (status === "Approved" || status === "Paid") {
    return ColorV2.PositiveGreenLight;
  }
  if (status === "Failed") return ColorV2.NegativeRedLight;
  return ColorV2.Blue7;
};

const spinnerRotation = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const getStatusTextColor = (status: string): string => {
  if (status === "Approved" || status === "Paid") {
    return ColorV2.PositiveGreen;
  }

  if (status === "Failed") return ColorV2.NegativeRed;
  return ColorV2.Blue4;
};

export const TransactionPeriodBar = styled.div`
  align-items: center;
  background-color: ${ColorV2.White};
  border-radius: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.md}px;
  justify-content: space-between;
  min-height: 104px;
  padding: ${Spacing.md}px ${Spacing.lg}px;
  width: 100%;

  ${mediaMax.tablet} {
    align-items: flex-start;
    flex-direction: column;
  }

  ${mediaMax.mobile} {
    min-height: 88px;
    padding: ${Spacing.sm}px;
  }
`;

export const TransactionSummary = styled.div`
  display: grid;
  flex: 1 1 900px;
  gap: ${Spacing.md}px;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  justify-content: flex-end;
  max-width: 1064px;
  min-width: 0;
  width: 100%;

  ${mediaMax.tablet} {
    flex-basis: 100%;
    width: 100%;
  }

  ${mediaMax.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const TransactionSummaryCard = styled.button<{ $isPrimary?: boolean }>`
  align-items: center;
  appearance: none;
  background-color: ${(props) =>
    props.$isPrimary ? ColorV2.Blue1 : ColorV2.White};
  border: 1.5px solid
    ${(props) => (props.$isPrimary ? ColorV2.Blue1 : ColorV2.Blue6)};
  border-radius: 8px;
  color: ${(props) => (props.$isPrimary ? ColorV2.White : ColorV2.Blue1)};
  cursor: pointer;
  display: flex;
  font: inherit;
  gap: ${Spacing.sm}px;
  min-width: 0;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  text-align: left;
  width: 100%;
`;

export const TransactionSummaryIcon = styled.span<{ $isPrimary?: boolean }>`
  align-items: center;
  display: inline-flex;
  flex-shrink: 0;
  height: 24px;
  justify-content: center;
  width: 24px;
`;

export const TransactionMethodIcon = styled.img`
  display: block;
  height: 20px;
  width: 20px;
`;

export const TransactionSummaryValue = styled.div<{ $isPrimary?: boolean }>`
  ${TypographyV2.h5}
  color: ${(props) => (props.$isPrimary ? ColorV2.White : ColorV2.Blue1)};
`;

export const TransactionSummaryLabel = styled.div<{ $isPrimary?: boolean }>`
  ${TypographyV2.s1}
  color: ${(props) => (props.$isPrimary ? ColorV2.White : ColorV2.Blue4)};
`;

export const TransactionCard = styled.section`
  background-color: ${ColorV2.White};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg}px;
  padding: ${Spacing.lg}px;
`;

export const TransactionTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const TransactionTitle = styled.h2`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
  margin: 0;
`;

export const TransactionHeader = styled.p`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
  margin: 0;
`;

export const TransactionLoading = styled.div`
  ${TypographyV2.b2}
  align-items: center;
  color: ${ColorV2.Blue4};
  display: flex;
  justify-content: center;
  min-height: 220px;
`;

export const TransactionLoadError = styled.p`
  ${TypographyV2.b2}
  color: ${ColorV2.NegativeRed};
  margin: 0;
`;

export const TransactionTable = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
  overflow-x: auto;
`;

const transactionGridColumns =
  "minmax(120px, 0.7fr) minmax(220px, 1.5fr) minmax(120px, 0.7fr) minmax(120px, 0.7fr) minmax(120px, 0.7fr) minmax(150px, 0.8fr) minmax(180px, 1fr)";

export const TransactionTableHead = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns: ${transactionGridColumns};
  padding-bottom: ${Spacing.sm}px;
`;

export const TransactionTableRow = styled.div`
  ${TypographyV2.b2}
  align-items: center;
  color: ${ColorV2.Blue1};
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns: ${transactionGridColumns};
  min-height: 52px;

  ${mediaMax.tablet} {
    align-items: flex-start;
    border-top: 1px solid ${ColorV2.Blue6};
    display: flex;
    flex-direction: column;
    gap: ${Spacing.xs}px;
    padding: ${Spacing.sm}px 0;
  }
`;

export const TransactionLoadMoreTrigger = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 56px;
  width: 100%;
`;

export const TransactionTableCell = styled.div`
  min-width: 0;
`;

export const TransactionWorker = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing.sm}px;
  min-width: 0;
`;

export const TransactionPlaceholderAvatar = styled.span`
  ${TypographyV2.s1}
  align-items: center;
  background: ${ColorV2.Blue6};
  border-radius: 999px;
  color: ${ColorV2.Blue1};
  display: inline-flex;
  flex-shrink: 0;
  height: 24px;
  justify-content: center;
  width: 24px;
`;

export const TransactionAmount = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
`;

export const TransactionStatusPill = styled.span<{ $status: string }>`
  ${TypographyV2.b2}
  align-items: center;
  background-color: ${(props) => getStatusBackgroundColor(props.$status)};
  border-radius: 999px;
  color: ${(props) => getStatusTextColor(props.$status)};
  display: inline-flex;
  gap: 6px;
  min-height: 32px;
  padding: 0 ${Spacing.md}px;
  white-space: nowrap;
`;

export const TransactionSpinner = styled.span<{ $size: number }>`
  animation: ${spinnerRotation} 0.8s linear infinite;
  border: 2px solid ${ColorV2.Blue6};
  border-radius: 50%;
  border-top-color: ${ColorV2.Blue1};
  display: inline-block;
  height: ${(props) => props.$size}px;
  width: ${(props) => props.$size}px;
`;
